import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Building2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import WhatsAppConfirmDialog from './WhatsAppConfirmDialog';
import { getWhatsAppLink } from './WhatsAppButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CorporateTraining = () => {
  const [formData, setFormData] = useState({
    organizationName: '',
    contactPerson: '',
    email: '',
    trainingRequirement: '',
    participants: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [whatsAppLink, setWhatsAppLink] = useState('');

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const link = getWhatsAppLink('corporate-training', formData);
      const response = await axios.post(`${BACKEND_URL}/api/corporate-training`, formData);
      if (response.data.success) {
        toast.success('Thank you! We will send you a training proposal soon.');
        setWhatsAppLink(link);
        setShowWhatsApp(true);
        setFormData({
          organizationName: '',
          contactPerson: '',
          email: '',
          trainingRequirement: '',
          participants: '',
        });
      }
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <WhatsAppConfirmDialog
      isOpen={showWhatsApp}
      onClose={setShowWhatsApp}
      whatsAppLink={whatsAppLink}
      formType="corporate-training"
    />
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0B0F1A]">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Info */}
          <div className="space-y-8">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center shadow-lg shadow-[#00E5FF]/50">
                <Building2 className="w-8 h-8 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-4xl lg:text-5xl font-bold font-['Space_Grotesk']">
                  Corporate <span className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] bg-clip-text text-transparent">Training</span>
                </h2>
                <p className="text-lg text-[#9CA3AF] mt-2">Customized, business-aligned programs</p>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden border-2 border-[#00E5FF]/30 shadow-2xl shadow-[#00E5FF]/20">
              <img
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB0cmFpbmluZ3xlbnwwfHx8fDE3NzAyMDgzODJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Corporate Training"
                className="w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0B0F1A]/60 to-transparent"></div>
            </div>

            <div className="bg-[#111827] border-2 border-[#00E5FF]/20 rounded-xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-white mb-4 font-['Space_Grotesk']">Why Choose Our Training?</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-[#00E5FF]/20 border border-[#00E5FF] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF]"></div>
                  </div>
                  <span className="text-[#9CA3AF]">Industry-experienced trainers</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-[#00E5FF]/20 border border-[#00E5FF] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF]"></div>
                  </div>
                  <span className="text-[#9CA3AF]">Hands-on, project-based learning</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-[#00E5FF]/20 border border-[#00E5FF] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF]"></div>
                  </div>
                  <span className="text-[#9CA3AF]">Customized curriculum for your team</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-[#00E5FF]/20 border border-[#00E5FF] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF]"></div>
                  </div>
                  <span className="text-[#9CA3AF]">Post-training support & certification</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Side - Form */}
          <Card className="shadow-2xl shadow-[#00E5FF]/10 border-2 border-[#00E5FF]/30 bg-[#111827]">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-white mb-6 font-['Space_Grotesk']">Request Training Proposal</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="organizationName" className="text-[#F9FAFB]">Organization Name *</Label>
                  <Input
                    id="organizationName"
                    type="text"
                    value={formData.organizationName}
                    onChange={(e) => handleChange('organizationName', e.target.value)}
                    required
                    className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] placeholder:text-[#9CA3AF] focus:border-[#00E5FF]"
                    placeholder="Your company name"
                  />
                </div>

                <div>
                  <Label htmlFor="contactPerson" className="text-[#F9FAFB]">Contact Person *</Label>
                  <Input
                    id="contactPerson"
                    type="text"
                    value={formData.contactPerson}
                    onChange={(e) => handleChange('contactPerson', e.target.value)}
                    required
                    className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] placeholder:text-[#9CA3AF] focus:border-[#00E5FF]"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#F9FAFB]">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] placeholder:text-[#9CA3AF] focus:border-[#00E5FF]"
                    placeholder="contact@company.com"
                  />
                </div>

                <div>
                  <Label htmlFor="trainingRequirement" className="text-[#F9FAFB]">Training Requirement *</Label>
                  <Textarea
                    id="trainingRequirement"
                    value={formData.trainingRequirement}
                    onChange={(e) => handleChange('trainingRequirement', e.target.value)}
                    required
                    className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] placeholder:text-[#9CA3AF] focus:border-[#00E5FF]"
                    rows={4}
                    placeholder="Describe the training topics and objectives..."
                  />
                </div>

                <div>
                  <Label htmlFor="participants" className="text-[#F9FAFB]">Number of Participants *</Label>
                  <Input
                    id="participants"
                    type="text"
                    value={formData.participants}
                    onChange={(e) => handleChange('participants', e.target.value)}
                    required
                    className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] placeholder:text-[#9CA3AF] focus:border-[#00E5FF]"
                    placeholder="e.g., 20-30 employees"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold py-6 text-lg rounded-lg shadow-lg shadow-[#00E5FF]/40 hover:shadow-[#00E5FF]/60 transition-all duration-300"
                >
                  {isSubmitting ? 'Submitting...' : 'Request Training Proposal'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
    </>
  );
};

export default CorporateTraining;
